import Link from 'next/link'
import { useLayoutEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { t } from 'localization'
import {
  collectionsDirectoryExpandTrackEvent,
  collectionsDirectoryGroupTrackEvent,
  collectionsDirectoryLinkTrackEvent,
  collectionsDirectorySectionTrackEvent,
} from 'mParticle/footerTrackEvents'
import { ChevronDown } from 'shared/components/Icons/SVGIcons/ChevronDown'
import { CollectionsDirectoryQA } from 'shared/dataAttributes'
import { colors as fc2Colors } from 'shared/lib'
import { colors, fonts, mediaQueries } from 'shared/theme'
import {
  CollectionsDirectory as CollectionsDirectoryData,
  ICollectionsDirectoryLink,
} from 'shared/types/CollectionsDirectory'

const TP = 'layout.footer.collectionsDirectory'

const TAB_FADE_MS = 180
const EXPAND_TRANSITION_MS = 350
const COLLAPSED_ROWS = 3
const DESKTOP_COLUMNS = 6
const SHORT_LABEL_MAX = 30

type LinkLayout = 'row' | 'grid' | 'grid-wrap'

export const getLinkLayout = (links: ICollectionsDirectoryLink[]): LinkLayout => {
  const longest = links.reduce((max, { label }) => Math.max(max, label.length), 0)

  if (links.length <= DESKTOP_COLUMNS && longest <= SHORT_LABEL_MAX) return 'row'

  return longest > SHORT_LABEL_MAX ? 'grid-wrap' : 'grid'
}

interface ICollectionsDirectoryProps {
  sections?: CollectionsDirectoryData
}

/**
 * Link directory rendered above the footer
 *
 * Every section and group is present in the server-rendered markup so crawlers
 * see all links; inactive ones are hidden with CSS rather than unmounted.
 */
const CollectionsDirectory = ({ sections }: ICollectionsDirectoryProps) => {
  const [activeSection, setActiveSection] = useState(0)
  const [activeGroups, setActiveGroups] = useState<Record<number, number>>({})
  const [isFading, setIsFading] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [fullHeight, setFullHeight] = useState(0)
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null)

  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const gridRef = useRef<HTMLDivElement>(null)

  const activeGroup = activeGroups[activeSection] ?? 0
  const currentSection = sections?.[activeSection]
  const currentLinks = currentSection?.groups.length
    ? currentSection.groups[activeGroup]?.links ?? []
    : currentSection?.links ?? []
  const currentLayout = getLinkLayout(currentLinks)

  useLayoutEffect(() => {
    setIsExpanded(false)

    const grid = gridRef.current
    if (!grid || currentLayout === 'row' || !grid.children.length) {
      setCollapsedHeight(null)
      setFullHeight(grid?.scrollHeight ?? 0)
      return
    }

    const items = Array.from(grid.children) as HTMLElement[]
    const containerTop = grid.getBoundingClientRect().top
    const itemTops = items.map((item) =>
      Math.round(item.getBoundingClientRect().top - containerTop),
    )
    const rowTops = Array.from(new Set(itemTops)).sort((a, b) => a - b)

    setFullHeight(grid.scrollHeight)

    if (rowTops.length <= COLLAPSED_ROWS) {
      setCollapsedHeight(null)
      return
    }

    const lastVisibleRowTop = rowTops[COLLAPSED_ROWS - 1]
    const rowBottoms = items
      .filter((_, index) => itemTops[index] === lastVisibleRowTop)
      .map((item) => Math.round(item.getBoundingClientRect().bottom - containerTop))

    setCollapsedHeight(Math.max(...rowBottoms))
  }, [activeSection, activeGroup, currentLayout])

  useLayoutEffect(() => () => clearTimeout(fadeTimeoutRef.current), [])

  if (!sections?.length) return null

  const handleSectionChange = (index: number) => {
    if (index === activeSection) return

    collectionsDirectorySectionTrackEvent({
      previousSectionName: sections[activeSection]?.label ?? '',
      sectionName: sections[index]?.label ?? '',
    })

    setIsFading(true)
    clearTimeout(fadeTimeoutRef.current)
    fadeTimeoutRef.current = setTimeout(() => {
      setActiveSection(index)
      setIsFading(false)
    }, TAB_FADE_MS)
  }

  const handleGroupChange = (sectionIndex: number, groupIndex: number) => {
    const section = sections[sectionIndex]
    const previousGroupIndex = activeGroups[sectionIndex] ?? 0

    if (groupIndex !== previousGroupIndex) {
      collectionsDirectoryGroupTrackEvent({
        groupName: section?.groups[groupIndex]?.label ?? '',
        previousGroupName: section?.groups[previousGroupIndex]?.label ?? '',
        sectionName: section?.label ?? '',
      })
    }

    setActiveGroups((current) => ({ ...current, [sectionIndex]: groupIndex }))
  }

  const handleExpandChange = (nextExpanded: boolean) => {
    collectionsDirectoryExpandTrackEvent({
      groupName: currentSection?.groups[activeGroup]?.label ?? '',
      isExpanded: nextExpanded,
      sectionName: currentSection?.label ?? '',
    })

    setIsExpanded(nextExpanded)
  }

  const isCollapsible = collapsedHeight !== null
  const wrapperHeight = isCollapsible && !isExpanded ? collapsedHeight : fullHeight

  return (
    <Wrapper
      aria-label={t(`${TP}.label`, 'Collections directory')}
      data-qa={CollectionsDirectoryQA.CollectionsDirectory}
    >
      <Content>
        <Header>
          <SectionTabs role="tablist">
            {sections.map((section, sectionIndex) => (
              <SectionTab
                aria-selected={sectionIndex === activeSection}
                data-qa={CollectionsDirectoryQA.CollectionsDirectorySectionTab}
                isActive={sectionIndex === activeSection}
                key={section.label}
                onClick={() => handleSectionChange(sectionIndex)}
                role="tab"
                type="button"
              >
                {section.label}
              </SectionTab>
            ))}
          </SectionTabs>

          {!!currentSection?.groups.length && (
            <GroupPills isFading={isFading} role="tablist">
              {currentSection.groups.map((group, groupIndex) => (
                <GroupPill
                  aria-selected={groupIndex === activeGroup}
                  data-qa={CollectionsDirectoryQA.CollectionsDirectoryGroupPill}
                  isActive={groupIndex === activeGroup}
                  key={group.label}
                  onClick={() => handleGroupChange(activeSection, groupIndex)}
                  role="tab"
                  type="button"
                >
                  {group.label}
                </GroupPill>
              ))}
            </GroupPills>
          )}
        </Header>

        <LinksWrapper style={fullHeight ? { height: wrapperHeight } : undefined}>
          <LinksContent isFading={isFading}>
            {sections.map((section, sectionIndex) => {
              const sectionGroup = activeGroups[sectionIndex] ?? 0
              const isActiveSection = sectionIndex === activeSection

              // Groups keep their own grid so each can be measured and hidden
              // independently while staying in the markup for crawlers.
              return section.groups.length ? (
                section.groups.map((group, groupIndex) => {
                  const isActive = isActiveSection && groupIndex === sectionGroup

                  return (
                    <LinkList
                      isActive={isActive}
                      key={`${section.label}-${group.label}`}
                      layout={getLinkLayout(group.links)}
                      ref={isActive ? gridRef : undefined}
                    >
                      {group.links.map(
                        renderLink({ groupName: group.label, sectionName: section.label }),
                      )}
                    </LinkList>
                  )
                })
              ) : (
                <LinkList
                  isActive={isActiveSection}
                  key={section.label}
                  layout={getLinkLayout(section.links)}
                  ref={isActiveSection ? gridRef : undefined}
                >
                  {section.links.map(renderLink({ groupName: '', sectionName: section.label }))}
                </LinkList>
              )
            })}
          </LinksContent>
          {isCollapsible && !isExpanded && (
            <LinksGradient onClick={() => handleExpandChange(true)} />
          )}
        </LinksWrapper>

        {isCollapsible && (
          <ExpandRow>
            <ExpandButton
              aria-expanded={isExpanded}
              aria-label={
                isExpanded ? t(`${TP}.showLess`, 'Show less') : t(`${TP}.showMore`, 'Show more')
              }
              data-qa={CollectionsDirectoryQA.CollectionsDirectoryToggleButton}
              isExpanded={isExpanded}
              onClick={() => handleExpandChange(!isExpanded)}
              type="button"
            >
              <ChevronDown color={colors.mediumGray} height="12" width="12" />
            </ExpandButton>
          </ExpandRow>
        )}
      </Content>
      <Divider />
    </Wrapper>
  )
}

/**
 * Builds the link renderer for one section/group so each tap can report the
 * tab and pill it was found under. `groupName` is empty for ungrouped sections.
 */
const renderLink =
  ({ groupName, sectionName }: { groupName: string; sectionName: string }) =>
  ({ href, label }: ICollectionsDirectoryLink) => (
    <DirectoryLink
      data-qa={CollectionsDirectoryQA.CollectionsDirectoryLink}
      href={href}
      key={`${href}-${label}`}
      onClick={() =>
        collectionsDirectoryLinkTrackEvent({
          groupName,
          sectionName,
          url: href,
          urlName: label,
        })
      }
    >
      {label}
    </DirectoryLink>
  )

const Wrapper = styled.nav`
  background-color: ${colors.black};
  color: ${colors.white};
  font-family: ${fonts.helvetica};
`

const Content = styled.div`
  margin-left: auto;
  margin-right: auto;
  max-width: 1600px;
  padding: 56px 24px;

  ${mediaQueries.tabletOrLarger} {
    padding-left: 40px;
    padding-right: 40px;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 56px;
`

const SectionTabs = styled.div`
  align-items: center;
  display: flex;
  gap: 24px;
  overflow-x: auto;

  ${mediaQueries.mobile} {
    margin: 0 -24px;
    padding: 0 24px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`

const SectionTab = styled.button<{ isActive: boolean }>`
  background: none;
  border: 0;
  color: ${({ isActive }) => (isActive ? colors.white : colors.mediumGray)};
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  padding: 0;
  text-transform: uppercase;
  white-space: nowrap;

  &:hover {
    color: ${({ isActive }) => (isActive ? colors.white : colors.offWhite)};
  }
`

const GroupPills = styled.div<{ isFading: boolean }>`
  align-items: center;
  display: flex;
  gap: 12px;
  opacity: ${({ isFading }) => (isFading ? 0 : 1)};
  transition: opacity ${TAB_FADE_MS}ms ease;

  ${mediaQueries.mobile} {
    margin: 0 -24px;
    overflow-x: auto;
    padding: 0 24px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`

const GroupPill = styled.button<{ isActive: boolean }>`
  align-items: center;
  background: ${({ isActive }) => (isActive ? colors.white : 'none')};
  border: 1px solid ${({ isActive }) => (isActive ? colors.white : colors.darkGray)};
  border-radius: 50px;
  color: ${({ isActive }) => (isActive ? colors.black : colors.mediumGray)};
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: ${({ isActive }) => (isActive ? 500 : 400)};
  justify-content: center;
  line-height: 1.25;
  padding: 7px 12px;
  white-space: nowrap;

  @media (hover: hover) and (pointer: fine) {
    &[aria-selected="false"]:hover {
      color: ${fc2Colors.FC2_DISABLED_GREY};
    }
  }
`

const LinksWrapper = styled.div`
  overflow: hidden;
  position: relative;
  transition: height ${EXPAND_TRANSITION_MS}ms
    linear(
      0,
      0.0914,
      0.2561,
      0.4199,
      0.5591,
      0.6696,
      0.7512,
      0.8159,
      0.8641,
      0.8999,
      0.9264,
      0.9459,
      0.9602,
      0.9707,
      0.9785,
      0.9842,
      0.9884,
      0.9915,
      0.9936,
      0.9953,
      0.9966,
      0.9975,
      0.9981,
      0.9986,
      1
    );
`

const LinksContent = styled.div<{ isFading: boolean }>`
  opacity: ${({ isFading }) => (isFading ? 0 : 1)};
  transition: opacity ${TAB_FADE_MS}ms ease;
`

/** Short lists sit on one line; longer ones fill a six column grid. */
const rowLayout = css`
  align-items: center;
  display: flex;
  gap: 40px;

  a {
    white-space: nowrap;
  }

  ${mediaQueries.mobile} {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(2, 1fr);

    a {
      white-space: normal;
    }
  }
`

const gridLayout = (isWrap: boolean) => css`
  display: grid;
  gap: ${isWrap ? '16px' : '24px'} 32px;
  grid-template-columns: repeat(${DESKTOP_COLUMNS}, 1fr);
  width: 100%;

  a {
    display: -webkit-box;
    line-height: ${isWrap ? 1.4 : 1.2};
    overflow: hidden;

    /* Long product titles wrap rather than stretching the column */
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }

  ${mediaQueries.mobile} {
    grid-template-columns: repeat(2, 1fr);
  }
`

/**
 * Keeps inactive groups in the markup for crawlers without showing them.
 *
 * The layout styles set `display` inside a media query, which would outrank a
 * plain `display: none` further down, so hiding has to win at every breakpoint.
 */
const LinkList = styled.div<{ isActive: boolean; layout: LinkLayout }>`
  ${({ layout }) => (layout === 'row' ? rowLayout : gridLayout(layout === 'grid-wrap'))}
  ${({ isActive }) =>
    !isActive &&
    css`
      &&& {
        display: none;
      }
    `}
`

const DirectoryLink = styled(Link)`
  color: ${colors.white};
  font-size: 14px;
  line-height: 1.2;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const LinksGradient = styled.div`
  background: linear-gradient(to bottom, rgb(0 0 0 / 40%) -24%, rgb(0 0 0 / 100%) 108%);
  bottom: 0;
  cursor: pointer;
  height: 60px;
  left: 0;
  mask-image: linear-gradient(to bottom, transparent, black 12%);
  pointer-events: auto;
  position: absolute;
  right: 0;
`

const ExpandRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: -24px;
  padding-top: 0;

  &:has([aria-expanded="true"]) {
    margin-top: 16px;
  }
`

const ExpandButton = styled.button<{ isExpanded: boolean }>`
  align-items: center;
  background: none;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 28px;
  justify-content: center;
  transform: ${({ isExpanded }) => (isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform ${EXPAND_TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1);
  width: 28px;

  ${mediaQueries.tabletOrLarger} and (hover: hover) and (pointer: fine) {
    &:hover svg path {
      fill: ${colors.white};
    }
  }
`

const Divider = styled.hr`
  background-color: ${colors.nearBlack};
  border: none;
  display: block;
  height: 1px;
  margin: 0;
  width: 100%;
`

export default CollectionsDirectory
